import React, { useEffect, useState } from 'react';
import { OrganizerStats } from './components/OrganizerStats';
import { RecentPilgrims } from './components/RecentPilgrims';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { useGuidesStore } from '../../stores/guidesStore';
import { usePilgrimGuideStore } from '../../stores/pilgrimGuideStore';
import { useDisputesStore } from '../../stores/disputesStore';
import { useAccommodationStore } from '../../stores/accommodationStore';
import { useUserVerification } from '../../hooks/useUserVerification';
import { ContractViewer } from '../../components/contracts/ContractViewer';
import { Building2, User, AlertTriangle, Users, FileText } from 'lucide-react';

export const OrganizerDashboard = () => {
  const userId = localStorage.getItem('userId') || '';
  const [isLoading, setIsLoading] = useState(true);
  
  // Get user verification data
  const { verification: userVerification, loading: verificationLoading } = useUserVerification(userId);
  
  // Get store functions but don't use links
  const {
    guides,
    loading: guidesLoading, 
    error: guidesError, 
    initialized: guidesInitialized,
    initializeListener: initializeGuides 
  } = useGuidesStore();

  const { 
    initialized: linksInitialized
  } = usePilgrimGuideStore();

  const {
    tables,
    loading: tablesLoading,
    error: tablesError,
    fetchTables
  } = useAccommodationStore();

  // Initialize only necessary data when component mounts
  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      
      // Initialize guides listener if not already initialized
      if (!guidesInitialized) {
        initializeGuides();
      }

      // Initialize accommodation tables
      await fetchTables();
      
      setIsLoading(false);
    };

    initializeData();
  }, [
    guidesInitialized,
    initializeGuides,
    fetchTables
  ]);

  // Show loading state if any required data is still loading
  if (isLoading || guidesLoading || tablesLoading || verificationLoading) {
    return <LoadingSpinner />;
  }

  // Show error if any error occurred
  if (guidesError || tablesError) {
    return <div className="text-red-500">
      {guidesError || tablesError}
    </div>;
  }

  // Calculate available stats without links data
  const organizerGuides = Object.values(guides).filter(g => g.organizerId === userId);
  
  // Use placeholders for link-dependent stats
  const stats = {
    totalGuides: organizerGuides.length,
    // We don't have links data yet, so show placeholders
    totalPilgrims: linksInitialized ? '...' : 'Visit Links',
    pendingLinks: linksInitialized ? '...' : 'Visit Links',
    disputes: linksInitialized ? '...' : 'Visit Links',
    isLinksInitialized: linksInitialized
  };

  return (
    <div className="space-y-6">
      {/* Contract Section - Only show if verification is approved and has a contract */}
      {userVerification && userVerification.status === 'approved' && userVerification.contractUrl && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <FileText className="text-green-500" size={24} />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Your Contract</h2>
                <p className="text-sm text-gray-500">View the contract you signed</p>
              </div>
            </div>
            <ContractViewer
              contractUrl={userVerification.contractUrl}
              title="View Contract"
              className="px-4 py-2"
            />
          </div>
        </div>
      )}
      
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary-50">
              <Users size={24} className="text-primary-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Guides</p>
              <p className="text-2xl font-bold mt-1 text-primary-500">
                {stats.totalGuides}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <User size={24} className="text-blue-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Pilgrims</p>
              {stats.isLinksInitialized ? (
                <p className="text-2xl font-bold mt-1 text-blue-500">
                  {stats.totalPilgrims}
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-2 italic">
                  Visit the Pilgrim Guide page to see data
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-yellow-50">
              <User size={24} className="text-yellow-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Not Owner Pilgrims</p>
              {stats.isLinksInitialized ? (
                <p className="text-2xl font-bold mt-1 text-yellow-500">
                  {stats.pendingLinks}
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-2 italic">
                  Visit the Pilgrim Guide page to see data
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-50">
              <AlertTriangle size={24} className="text-red-500" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Disputes</p>
              {stats.isLinksInitialized ? (
                <p className="text-2xl font-bold mt-1 text-red-500">
                  {stats.disputes}
                </p>
              ) : (
                <p className="text-sm text-gray-400 mt-2 italic">
                  Visit the Pilgrim Guide page to see data
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Guides */}
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">Recent Guides</h2>
        </div>
        
        <div className="space-y-4">
          {organizerGuides.length > 0 ? (
            organizerGuides
              .sort((a, b) => b.createdAt - a.createdAt)
              .slice(0, 5)
              .map((guide) => (
                <div key={guide.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-50 rounded-full">
                      <User size={18} className="text-primary-500" />
                    </div>
                    <div>
                      <p className="font-medium">{guide.name}</p>
                      <p className="text-sm text-gray-500">{guide.email}</p>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(guide.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              No guides found for your organization
            </div>
          )}
        </div>
      </div>

      {/* Additional dashboard sections can be added here */}
    </div>
  );
};
